import * as ramda from 'ramda';

import type {
    EmailAutomation as EmailAutomationFragment,
    SalesAutomationStage as SalesAutomationStageFragment,
} from 'nutshell-graphql-types';

/**
 * Helper function used to translate an array of EmailAutomations and
 * their associated stages into a "board-ready" map by stageId.
 *
 * For example, we'll translate a flat array of subscriptions into something
 * like:
 *
 * {
 *   '1-stages': [$subscription1 (extends through stage 4), $subscription2 (extends through stage 2)],
 *   '2-stages': [         null,          null],
 *   '3-stages': [         null, $subscription3 (extends through stage 3)],
 *   '4-stages': [         null]
 * }
 *
 * which represents
 *
 * [     New    ][ Prospecting ][  Decision     ][   Closing   ]
 * [=======subscription1=======================================]
 * [=======subscription2=======][=subscription3=][      +      ]
 * [      +     ][      +      ][        +      ]
 *
 * Where each "row" above actually represents a column on the board, and a
 * `null` value is actually a placeholder.
 *
 * In the above example, `4-stages` only returns 1 item, that's because
 * there's only 1 placeholder necessary in that column. We want the plus
 * button to float up as far as it can.
 *
 * @param  {Object[]} allSubscriptions  - Flat list of all EmailAutomations
 *                                        for the stageset. This function expects
 *                                        this list to be ordered already from the
 *                                        backend.
 * @param  {Object[]} stages:           - Flat list of stages related to this given
 *                                        stageset
 * @return {Object}                     - null|EmailAutomation arrays,
 *                                        key'd by stage id.
 */
export function translateStageSubscriptionsForBoard(
    allSubscriptions: EmailAutomationFragment[] | null | undefined,
    stages: SalesAutomationStageFragment[]
): {
    [key: string]: EmailAutomationFragment[];
} {
    // First, verify that stages all have unique positions, otherwise we're in trouble.
    if (!arePositionsUnique(stages)) {
        const dupeStageIds = findDuplicatePositionStages(stages).map((stage) => stage.id);
        Nut.error(`Non-unique (duplicate) stage positions detected: ${dupeStageIds.join(', ')}`);

        throw new Error('Non-unique stage positions.');
    }

    // Start with an initial value, which an object key'd by stage _position_,
    // with each key's value being an empty array:
    //
    // ['1']: [],
    // ['2']: [],
    // ['3']: [],
    // ['4']: [],
    const subscriptionsByPositionDefaultValue = stages.reduce<Record<string, any>>((acc, stage) => {
        return {
            ...acc,
            [stage.position]: [],
        };
    }, {});

    // We support passing `null` as allSubscriptions, so if that's the case,
    // just pretend our array is empty. We can't just bail, because our
    // return expects an object key'd by stageId
    const subscriptions = allSubscriptions ? allSubscriptions : [];

    // We'll keep an array of ids that have already been placed on the board
    // @ts-expect-error
    const subscriptionIdsPlacedOnBoard = [];

    // It's much easier to recursively just fill arrays if each one is
    // key'd by position rather than id (saves an additional lookup)
    // so we'll just store everything by position initially
    const subscriptionsByStagePosition = stages.reduce((existingSubscriptions, stage) => {
        const subscriptionsForStage = subscriptions.filter((subscription) => {
            return subscription.entersStage.id === stage.id;
        });

        const newSubscriptions = subscriptionsForStage.reduce<Record<string, any>>(
            (acc, subscription) => {
                // There's a chance we've iterated over this subscription
                // already in a previous row - skip it if that's the case
                // @ts-expect-error
                if (subscriptionIdsPlacedOnBoard.includes(subscription.id)) {
                    return existingSubscriptions;
                }

                // If it's a new subscription, fill remaining row space
                return fillRemainingRowSpaceWithSubscription(existingSubscriptions, subscription);
            },
            {}
        );

        return {
            ...existingSubscriptions,
            ...newSubscriptions,
        };
    }, subscriptionsByPositionDefaultValue);

    // Translate our by-stage-position array into a by-id array before returning
    return getSubscriptionsKeyedByStageId(subscriptionsByStagePosition, stages);

    // Recursively called function to fill remaining row space with a board
    // subscription. Will continue to call itself until the entire row
    // is filled, or there are no valid, unused board subscriptions that
    // fit in that row.
    // @ts-expect-error
    function fillRemainingRowSpaceWithSubscription(boardSubs, sub: EmailAutomationFragment) {
        if (!boardSubs[sub.entersStage.position]) {
            boardSubs[sub.entersStage.position] = [];
        }

        boardSubs[sub.entersStage.position].push(sub);
        subscriptionIdsPlacedOnBoard.push(sub.id);

        // Current row we're filling
        const currentRow = boardSubs[sub.entersStage.position].length;

        // Fills the row with "placeholders" for as long as our subscription
        // extends, i.e. `numberOfStageSpanned + 1`
        for (
            let pos = sub.entersStage.position + 1;
            pos < sub.entersStage.position + sub.numberOfStagesSpanned;
            pos++
        ) {
            if (!boardSubs[pos]) {
                boardSubs[pos] = [];
            }

            // We might need to backfill a _column_ with null placeholders
            // if we're dealing with an overlapping card.
            //
            // For example when placing subscription 2 on this board:
            //
            // [ 101-stages ][ 102-stages ][ 103-stages ][ 104-stages ]
            // [====sub1==================][    null    ][     +      ]
            // [     +      ][====sub2==================]
            //               [     +      ][     +      ]
            //
            // We need to backfill 103-stages with a `null` value which was
            // _not_ accounted for when placing subscription 1
            const emptySpacesToBackfill = currentRow - boardSubs[pos].length;
            for (let i = 0; i < emptySpacesToBackfill; i++) {
                boardSubs[pos].push(null);
            }
        }

        const nextValidSubscription = getNextValidUnusedSubscriptionForRow(sub);
        if (nextValidSubscription) {
            boardSubs = fillRemainingRowSpaceWithSubscription(boardSubs, nextValidSubscription);
        }

        return boardSubs;
    }

    // Helper function to get the next valid, unused subscription _after_
    // the current subscription (passed in as a parameter)
    //
    // This function returns either `null` or the next valid, unused subscription
    function getNextValidUnusedSubscriptionForRow(sub: EmailAutomationFragment) {
        const position = sub.entersStage.position;
        const nextPosition = position + sub.numberOfStagesSpanned;

        // This means our current subscription has filled up the
        // remainder of the current's row space
        if (nextPosition > stages.length) {
            return null;
        }

        let validSubscription = null;
        for (let pos = nextPosition; pos <= stages.length; pos++) {
            // Filtering out already used ids first for minor optimisation
            const unusedSubscriptions = subscriptions.filter((subcription) => {
                // @ts-expect-error
                return !subscriptionIdsPlacedOnBoard.includes(subcription.id);
            });

            validSubscription = unusedSubscriptions.find((subscription) => {
                return (
                    subscription.entersStage.position === pos &&
                    subscription.numberOfStagesSpanned - 1 + pos <= stages.length
                );
            });

            // If we've found a valid subscription, break out of loop
            if (validSubscription) break;
        }

        return validSubscription;
    }
}

// @ts-expect-error
function getSubscriptionsKeyedByStageId(subscriptionsByPosition, stages) {
    return Object.keys(subscriptionsByPosition).reduce<Record<string, any>>((byId, position) => {
        // @ts-expect-error
        const stageForPosition = stages.find((stage) => stage.position === Number(position));

        if (!stageForPosition) {
            return byId;
        }

        return {
            ...byId,
            [stageForPosition.id]: subscriptionsByPosition[position],
        };
    }, {});
}

// Check that there are no duplicate position values
// @ts-expect-error
function arePositionsUnique(stages) {
    // @ts-expect-error
    const positions = stages.map((stage) => stage.position);
    const positionSet = new Set(positions);

    return positionSet.size === positions.length;
}

// Return an array of stages which all have the same position.  Assumes
// that there is no more than one set of duplicate positions.
export function findDuplicatePositionStages(stages: SalesAutomationStageFragment[]) {
    // @ts-expect-error
    const positions = ramda.groupBy((x) => String(x.position))(stages);
    const dupeStages = Object.keys(positions).reduce<Array<any>>((acc, position) => {
        // @ts-expect-error
        if (positions[position].length > 1) {
            return acc.concat(positions[position]);
        }

        return acc;
    }, []);

    return dupeStages;
}
